import { useState } from "react";
import { useI18n } from "../../i18n";
import { mockSearch, type MockSearchResult } from "../../lib/mock";

const HISTORY_KEY = "deskflow.searchHistory";

function loadHistory(): string[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}

export function Search(): React.JSX.Element {
  const { language, t } = useI18n();
  const [query, setQuery] = useState("");
  const [searchedFor, setSearchedFor] = useState("");
  const [results, setResults] = useState<MockSearchResult[]>([]);
  const [history, setHistory] = useState(loadHistory);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const search = (value = query): void => {
    const nextQuery = value.trim();
    if (!nextQuery) return;
    const nextHistory = [
      nextQuery,
      ...history.filter((item) => item !== nextQuery),
    ].slice(0, 6);
    setQuery(nextQuery);
    setSearchedFor(nextQuery);
    setResults(mockSearch(nextQuery, language));
    setHistory(nextHistory);
    setExpandedId(null);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(nextHistory));
  };

  return (
    <div className="search-page">
      <section className="search-hero">
        <div className="search-box">
          <span aria-hidden="true">⌕</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") search();
            }}
            placeholder={t("search.placeholder")}
            aria-label={t("search.label")}
          />
          <button
            className="primary"
            type="button"
            disabled={!query.trim()}
            onClick={() => search()}
          >
            {t("search.button")}
          </button>
        </div>
        <p>{t("search.helper")}</p>
        {history.length > 0 && (
          <div className="search-history">
            <small>{t("search.recent")}</small>
            {history.map((item) => (
              <button type="button" key={item} onClick={() => search(item)}>
                {item}
              </button>
            ))}
          </div>
        )}
      </section>

      {results.length === 0 ? (
        <section className="panel empty-state search-empty">
          <div className="empty-icon">⌕</div>
          <h2>{t("search.emptyTitle")}</h2>
          <p>{t("search.emptyBody")}</p>
        </section>
      ) : (
        <section className="search-results">
          <div className="search-result-heading">
            <div>
              <small>{t("search.mockLabel")}</small>
              <h2>
                {t("search.resultsFor")} “{searchedFor}”
              </h2>
            </div>
            <span>{results.length}</span>
          </div>
          {results.map((result) => (
            <article className="panel search-result" key={result.id}>
              <div className="result-source">{result.source}</div>
              <h3>{result.title}</h3>
              <p>{result.snippet}</p>
              <div className="ai-summary">
                <span>✦</span>
                <div>
                  <small>{t("search.aiSummary")}</small>
                  <p>{result.aiSummary}</p>
                </div>
              </div>
              <button
                type="button"
                className="text-button"
                onClick={() =>
                  setExpandedId((current) =>
                    current === result.id ? null : result.id,
                  )
                }
              >
                {expandedId === result.id ? t("search.less") : t("search.more")}
              </button>
              {expandedId === result.id && (
                <p className="mock-detail">{t("search.mockDetail")}</p>
              )}
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
